'use client';

// Login gate screen. Two modes:
//   • First run (no keys configured yet): "Create your access key" — generates
//     the owner's first key, logs in, and shows the key to save.
//   • Normal: "Enter access key" to unlock.
// Rendered bare (no app chrome) — AppShell treats /unlock as a public route.

import { useState, useEffect } from 'react';

export default function UnlockPage() {
    const [configured, setConfigured] = useState(null); // null = loading
    const [key, setKey] = useState('');
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState('');
    const [createdKey, setCreatedKey] = useState('');

    useEffect(() => {
        fetch('/api/auth/unlock')
            .then(r => r.json())
            .then(d => setConfigured(!!d.configured))
            .catch(() => setConfigured(true));
    }, []);

    function nextUrl() {
        try {
            const p = new URLSearchParams(window.location.search).get('next');
            // Must be an app-internal path. Reject protocol-relative "//evil.com"
            // (starts with '/' but is an off-site redirect) and any scheme.
            return p && /^\/(?!\/)/.test(p) ? p : '/';
        } catch { return '/'; }
    }

    async function submitKey(e) {
        e?.preventDefault();
        setError(''); setBusy(true);
        try {
            const r = await fetch('/api/auth/unlock', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ key }),
            }).then(x => x.json());
            if (r.success) { window.location.href = nextUrl(); return; }
            setError(r.error || 'Could not unlock.');
        } catch (e) { setError(e.message); }
        setBusy(false);
    }

    async function setupKey() {
        setError(''); setBusy(true);
        try {
            const r = await fetch('/api/auth/unlock', {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ setup: true, label: 'Owner key' }),
            }).then(x => x.json());
            if (r.success && r.created) { setCreatedKey(r.created); return; }
            setError(r.error || 'Setup failed.');
        } catch (e) { setError(e.message); }
        setBusy(false);
    }

    const S = {
        page: { minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'radial-gradient(1200px 600px at 50% -10%, rgba(37, 99, 235,0.18), transparent), #f7f9fc', padding: 20, fontFamily: 'Outfit, system-ui, sans-serif' },
        card: { width: 'min(420px, 100%)', background: 'rgba(15,23,42,0.03)', border: '1px solid rgba(15,23,42,0.08)', borderRadius: 18, padding: 32, boxShadow: '0 30px 80px rgba(0,0,0,0.5)' },
        logo: { fontSize: 22, fontWeight: 800, color: '#0f172a', textAlign: 'center', marginBottom: 4 },
        sub: { color: '#475569', fontSize: 13.5, textAlign: 'center', margin: '0 0 24px', lineHeight: 1.5 },
        label: { display: 'block', fontSize: 12, fontWeight: 700, color: '#1d4ed8', marginBottom: 8, letterSpacing: 0.3 },
        input: { width: '100%', background: '#ffffff', color: '#0f172a', border: '1px solid rgba(15,23,42,0.14)', borderRadius: 10, padding: '13px 14px', fontSize: 15, letterSpacing: 1, textAlign: 'center', boxSizing: 'border-box', fontFamily: 'monospace' },
        btn: { width: '100%', marginTop: 16, background: 'linear-gradient(135deg,#2563eb,#2563eb)', border: 'none', color: '#fff', fontWeight: 800, fontSize: 15, padding: '13px', borderRadius: 10, cursor: 'pointer' },
        err: { color: '#dc2626', fontSize: 13, textAlign: 'center', marginTop: 12 },
        keyBox: { background: 'rgba(16,185,129,0.1)', border: '1px solid rgba(16,185,129,0.4)', borderRadius: 12, padding: 16, textAlign: 'center', margin: '16px 0' },
        keyText: { fontFamily: 'monospace', fontSize: 20, fontWeight: 800, color: '#059669', letterSpacing: 2, wordBreak: 'break-all' },
    };

    if (configured === null) {
        return <div style={S.page}><div style={{ color: '#475569' }}>Loading…</div></div>;
    }

    // First-run: created key just now — show it to save, then continue.
    if (createdKey) {
        return (
            <div style={S.page}>
                <div style={S.card}>
                    <div style={S.logo}>🔐 Your access key</div>
                    <p style={S.sub}>Save this key somewhere safe. You'll use it to log in, and you can give copies to people you want to grant access. You can create more or revoke it later in Settings.</p>
                    <div style={S.keyBox}>
                        <div style={S.keyText}>{createdKey}</div>
                    </div>
                    <button style={S.btn} onClick={() => { navigator.clipboard?.writeText(createdKey).catch(() => {}); window.location.href = '/'; }}>
                        Copy &amp; enter the app →
                    </button>
                </div>
            </div>
        );
    }

    // First-run: no keys yet → setup.
    if (!configured) {
        return (
            <div style={S.page}>
                <div style={S.card}>
                    <div style={S.logo}>🔐 Secure your app</div>
                    <p style={S.sub}>No access key is set up yet, so anyone can open this tool. Create your access key now to lock it down. Only people with the key will be able to get in.</p>
                    <button style={S.btn} disabled={busy} onClick={setupKey}>{busy ? 'Creating…' : 'Create my access key'}</button>
                    {error && <div style={S.err}>{error}</div>}
                </div>
            </div>
        );
    }

    // Normal: enter key.
    return (
        <div style={S.page}>
            <form style={S.card} onSubmit={submitKey}>
                <div style={S.logo}>🔐 Welcome back</div>
                <p style={S.sub}>Enter your access key to continue.</p>
                <label style={S.label}>ACCESS KEY</label>
                <input
                    style={S.input}
                    value={key}
                    onChange={e => setKey(e.target.value)}
                    placeholder="SLG-XXXX-XXXX-XXXX"
                    autoFocus
                    autoComplete="off"
                />
                <button style={S.btn} type="submit" disabled={busy}>{busy ? 'Unlocking…' : 'Unlock'}</button>
                {error && <div style={S.err}>{error}</div>}
            </form>
        </div>
    );
}
